import { useEffect, useRef, useState } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { api, type Block, type BlockContent, type BlockType, type Page, type Package } from "../lib/api";
import { BLOCK_EXAMPLES } from "../lib/blockExamples";
import { BlockEditor } from "../components/pages/BlockEditors";

// ---------------------------------------------------------------------------
// Block type metadata
// ---------------------------------------------------------------------------
interface BlockMeta {
  type: BlockType;
  label: string;
  icon: string;
  description: string;
}

const BLOCK_TYPES: BlockMeta[] = [
  { type: "statement", label: "Statement", icon: "💬", description: "Large bold opening text with category label" },
  { type: "hero", label: "Hero", icon: "🌅", description: "Full-width heading with CTA" },
  { type: "full_bleed", label: "Full-screen image", icon: "🖼️", description: "Fill the screen with a dramatic photo" },
  { type: "split", label: "Image + text", icon: "◧", description: "Photo on one side, text on the other" },
  { type: "text", label: "Text", icon: "📝", description: "Markdown rich text" },
  { type: "image", label: "Image", icon: "🖼", description: "Single image with caption" },
  { type: "quote", label: "Quote", icon: "✍️", description: "Blockquote with attribution" },
  { type: "callout", label: "Callout", icon: "📣", description: "Highlighted callout box" },
  { type: "faq", label: "FAQ", icon: "❓", description: "Accordion Q&A list" },
  { type: "services", label: "Services", icon: "💼", description: "Pricing / service list" },
  { type: "process", label: "Process steps", icon: "🔢", description: "Numbered steps showing how you work" },
  { type: "gallery_row", label: "Gallery Row", icon: "▦", description: "Grid of images (2-4 cols)" },
  { type: "divider", label: "Divider", icon: "—", description: "Horizontal line or spacer" },
  { type: "video", label: "Video", icon: "🎬", description: "Embedded video with caption" },
  { type: "section", label: "Section", icon: "⊞", description: "Flex container (row or column) to group blocks" },
];

function blockPreviewText(b: Block): string {
  const c = b.Content;
  switch (b.BlockType) {
    case "hero": return c.heading || "(no heading)";
    case "full_bleed": return c.heading || "(no heading)";
    case "split": return c.heading || c.body?.slice(0, 60) || "(empty)";
    case "text": return (c.markdown || "").split("\n")[0].slice(0, 80) || "(empty)";
    case "image": return c.caption || c.asset_id || "(no image)";
    case "quote": return c.body ? `"${c.body.slice(0, 60)}"` : "(empty)";
    case "callout": return c.heading || c.body || "(empty)";
    case "faq": return `${c.faq_items?.length ?? 0} question(s)`;
    case "services": return `${c.services_items?.length ?? 0} service(s)`;
    case "gallery_row": return `${c.items?.length ?? 0} image(s), ${c.columns ?? 3} cols`;
    case "divider": return c.style ?? "line";
    case "video": return c.url || "(no URL)";
    case "statement": return c.heading ? c.heading.slice(0, 60) : "Statement heading...";
    case "process": return c.intro || `${(c.process_steps || []).length} steps`;
    case "section": return `${c.direction ?? "column"} · gap:${c.gap ?? "md"} · align:${c.align ?? "stretch"}`;
    default: return "";
  }
}

const BLOCK_COLORS: Record<BlockType, string> = {
  hero: "#6366f1",
  full_bleed: "#1d4ed8",
  split: "#0891b2",
  text: "#10b981",
  image: "#f59e0b",
  quote: "#8b5cf6",
  callout: "#ec4899",
  faq: "#0ea5e9",
  services: "#14b8a6",
  gallery_row: "#f97316",
  divider: "#94a3b8",
  video: "#ef4444",
  statement: "#6366f1",
  process: "#0ea5e9",
  gallery_list: "#a855f7",
  contact_form: "#059669",
  section: "#64748b",
};

// Per-block-type editor components live in
// web/src/components/pages/BlockEditors.tsx — imported here as <BlockEditor>.

// ---------------------------------------------------------------------------
// Sortable block row
// ---------------------------------------------------------------------------
function SortableBlock({
  block,
  expanded,
  onToggle,
  onDelete,
  onSave,
  savingBlock,
  packages,
}: {
  block: Block;
  expanded: boolean;
  onToggle: () => void;
  onDelete: () => void;
  onSave: (content: BlockContent) => Promise<void>;
  savingBlock: boolean;
  packages?: Package[];
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: block.ID });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    marginBottom: "0.5rem",
  };
  const color = BLOCK_COLORS[block.BlockType] ?? "#94a3b8";

  return (
    <div ref={setNodeRef} style={style}>
      <div style={{
        border: "1px solid var(--border)",
        borderRadius: expanded ? "0.5rem 0.5rem 0 0" : "0.5rem",
        overflow: "hidden",
      }}>
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          padding: "0.6rem 0.75rem",
          background: "var(--card)",
        }}>
          {/* Drag handle */}
          <span {...attributes} {...listeners} style={{ cursor: "grab", color: "var(--muted)", fontSize: "1rem", userSelect: "none", touchAction: "none" }}>⠿</span>
          {/* Type badge */}
          <span style={{ background: color, color: "#fff", fontSize: "0.7rem", fontWeight: 600, padding: "0.15rem 0.4rem", borderRadius: "0.3rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
            {block.BlockType}
          </span>
          {/* Preview */}
          <span style={{ flex: 1, color: "var(--muted)", fontSize: "0.9rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {blockPreviewText(block)}
          </span>
          {/* Controls */}
          <button className="btn-ghost" onClick={onToggle} style={{ padding: "0.25rem 0.5rem", fontSize: "0.8rem" }}>
            {expanded ? "↑ Collapse" : "↓ Edit"}
          </button>
          <button className="btn-ghost" onClick={onDelete} style={{ color: "#ef4444", padding: "0.25rem 0.5rem", fontSize: "0.8rem" }}>×</button>
        </div>
      </div>
      {expanded && (
        <BlockEditor block={block} onSave={onSave} saving={savingBlock} packages={packages} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared props for the recursive child tree
// ---------------------------------------------------------------------------
interface ChildTreeProps {
  parentId: string;
  allBlocks: Block[];
  depth: number;
  expandedBlocks: Set<string>;
  onToggle: (id: string) => void;
  onDelete: (id: string) => void;
  onSave: (id: string, content: BlockContent) => Promise<void>;
  onAddChild: (parentId: string, blockType: BlockType) => Promise<void>;
  savingBlock: string | null;
  addingBlock: string | null;
  packages?: Package[];
}

// ---------------------------------------------------------------------------
// Sortable row for a nested child block (drag handle + controls + recurse)
// ---------------------------------------------------------------------------
function SortableChildBlock({ block, parentId, allBlocks, depth, expandedBlocks, onToggle, onDelete, onSave, onAddChild, savingBlock, addingBlock, packages }: ChildTreeProps & { block: Block }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: block.ID,
    data: { parentId },
  });
  const childColor = BLOCK_COLORS[block.BlockType] ?? "#94a3b8";
  const isExpanded = expandedBlocks.has(block.ID);
  const isSection = block.BlockType === "section";

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1, marginBottom: ".35rem" }}
    >
      <div style={{ border: "1px solid var(--border)", borderRadius: isExpanded ? ".4rem .4rem 0 0" : ".4rem", overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", gap: ".4rem", padding: ".45rem .6rem", background: "var(--card)" }}>
          <span {...attributes} {...listeners} style={{ cursor: "grab", color: "var(--muted)", fontSize: "1rem", userSelect: "none", touchAction: "none" }}>⠿</span>
          <span style={{ background: childColor, color: "#fff", fontSize: ".68rem", fontWeight: 600, padding: ".12rem .35rem", borderRadius: ".25rem", textTransform: "uppercase", letterSpacing: ".04em", whiteSpace: "nowrap", flexShrink: 0 }}>
            {block.BlockType}
          </span>
          <span style={{ flex: 1, color: "var(--muted)", fontSize: ".85rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {blockPreviewText(block)}
          </span>
          <button className="btn-ghost" onClick={() => onToggle(block.ID)} style={{ padding: ".2rem .4rem", fontSize: ".75rem", whiteSpace: "nowrap" }}>
            {isExpanded ? "↑ Collapse" : "↓ Edit"}
          </button>
          <button className="btn-ghost" onClick={() => void onDelete(block.ID)} style={{ color: "#ef4444", padding: ".2rem .4rem", fontSize: ".75rem" }}>×</button>
        </div>
      </div>
      {isExpanded && (
        <BlockEditor block={block} onSave={(c) => onSave(block.ID, c)} saving={savingBlock === block.ID} packages={packages} />
      )}
      {isSection && (
        <ChildTree parentId={block.ID} allBlocks={allBlocks} depth={depth + 1}
          expandedBlocks={expandedBlocks} onToggle={onToggle} onDelete={onDelete}
          onSave={onSave} onAddChild={onAddChild}
          savingBlock={savingBlock} addingBlock={addingBlock} packages={packages} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Recursive child tree — each section level gets its own SortableContext
// ---------------------------------------------------------------------------
function ChildTree({ parentId, allBlocks, depth, expandedBlocks, onToggle, onDelete, onSave, onAddChild, savingBlock, addingBlock, packages }: ChildTreeProps) {
  const [showPicker, setShowPicker] = useState(false);

  const children = allBlocks
    .filter(b => b.ParentBlockID === parentId)
    .sort((a, b) => (a.SortOrder ?? 0) - (b.SortOrder ?? 0));

  const alpha = Math.max(0.15, 0.45 - depth * 0.08);

  return (
    <div style={{ marginLeft: "1.25rem", marginTop: ".3rem", paddingLeft: ".65rem", borderLeft: `2px solid rgba(100,116,139,${alpha})` }}>
      <SortableContext items={children.map(c => c.ID)} strategy={verticalListSortingStrategy}>
        {children.map(child => (
          <SortableChildBlock key={child.ID} block={child} parentId={parentId}
            allBlocks={allBlocks} depth={depth} expandedBlocks={expandedBlocks}
            onToggle={onToggle} onDelete={onDelete} onSave={onSave}
            onAddChild={onAddChild} savingBlock={savingBlock}
            addingBlock={addingBlock} packages={packages} />
        ))}
      </SortableContext>

      {showPicker ? (
        <div className="card" style={{ marginTop: ".4rem", padding: ".65rem" }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: ".4rem" }}>
            <strong style={{ fontSize: ".8rem" }}>Add block inside section</strong>
            <button className="btn-ghost" onClick={() => setShowPicker(false)} style={{ fontSize: ".75rem" }}>✕</button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: ".35rem" }}>
            {BLOCK_TYPES.map(bt => (
              <button key={bt.type} type="button" className="btn-ghost"
                disabled={addingBlock !== null}
                onClick={() => { void onAddChild(parentId, bt.type); setShowPicker(false); }}
                style={{ textAlign: "left", fontSize: ".8rem", padding: ".35rem .5rem" }}>
                {bt.icon} {bt.label}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <button className="btn-ghost" onClick={() => setShowPicker(true)}
          style={{ marginTop: ".3rem", width: "100%", fontSize: ".78rem", padding: ".35rem", border: "1px dashed var(--border)", borderRadius: ".35rem" }}>
          + Add block inside section
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main editor page
// ---------------------------------------------------------------------------
interface Props { pageId: string }

type Flash = "saved" | "published" | null;

export function PageEditorPage({ pageId }: Props) {
  const [page, setPage] = useState<Page | null>(null);
  const [packages, setPackages] = useState<Package[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [metaTitle, setMetaTitle] = useState("");
  const [metaDesc, setMetaDesc] = useState("");
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [unpublishing, setUnpublishing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [flash, setFlash] = useState<Flash>(null);
  const [expandedBlocks, setExpandedBlocks] = useState<Set<string>>(new Set());
  const [savingBlock, setSavingBlock] = useState<string | null>(null);
  const [deletingBlock, setDeletingBlock] = useState<string | null>(null);
  const [showAddBlock, setShowAddBlock] = useState(false);
  const [addingBlock, setAddingBlock] = useState<string | null>(null);
  const portfolioSlugRef = useRef<string>("");
  const publishBtnRef = useRef<HTMLButtonElement>(null);
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isDirtyRef = useRef(false);

  function showFlash(type: NonNullable<Flash>) {
    setFlash(type);
    setTimeout(() => setFlash(null), 2500);
  }

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const [p, portfolio, pkgs] = await Promise.all([
        api.getPage(pageId),
        api.getMyPortfolio().catch(() => null),
        api.listPackages().catch(() => [] as Package[]),
      ]);
      setPackages(pkgs ?? []);
      setPage(p);
      setTitle(p.Title);
      setSlug(p.Slug);
      setMetaTitle(p.MetaTitle ?? "");
      setMetaDesc(p.MetaDescription ?? "");
      if (portfolio) portfolioSlugRef.current = portfolio.Slug;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load page");
    } finally {
      setLoading(false);
    }
  }

  // `load` is intentionally omitted from deps: it is redefined every render and
  // the effect must only fire when `pageId` changes, not on every re-render.
  // Adding `load` would cause an infinite fetch loop. Wrap in useCallback if
  // the function needs to be called from outside this effect.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void load(); }, [pageId]);

  // Auto-save: debounce 3 s after title/slug/meta changes.
  useEffect(() => {
    if (!page) return;
    const changed = title !== page.Title || slug !== page.Slug ||
      metaTitle !== (page.MetaTitle ?? "") || metaDesc !== (page.MetaDescription ?? "");
    if (!changed) { isDirtyRef.current = false; return; }
    isDirtyRef.current = true;
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => { void handleSaveDraft(); }, 3000);
    return () => { if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current); };
  // handleSaveDraft is stable within a render cycle; page identity is the real dep.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, slug, metaTitle, metaDesc]);

  // Cmd+S / Ctrl+S to save draft.
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === "s") {
        e.preventDefault();
        void handleSaveDraft();
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, title, slug, metaTitle, metaDesc]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  async function handleSaveDraft() {
    if (!page) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await api.updatePage(page.ID, { title, slug, meta_title: metaTitle, meta_description: metaDesc });
      setPage(updated);
      setTitle(updated.Title);
      setSlug(updated.Slug);
      showFlash("saved");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function handlePublish() {
    if (!page) return;
    // Save title/slug first if they differ.
    if (title !== page.Title || slug !== page.Slug) {
      await api.updatePage(page.ID, { title, slug }).catch(() => {});
    }
    setPublishing(true);
    setError(null);
    try {
      const updated = await api.publishPage(page.ID);
      setPage(updated);
      setTitle(updated.Title);
      setSlug(updated.Slug);
      showFlash("published");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Publish failed");
    } finally {
      setPublishing(false);
    }
  }

  async function handleUnpublish() {
    if (!page || !window.confirm("Take this page offline? Visitors will no longer see it.")) return;
    setUnpublishing(true);
    setError(null);
    try {
      const updated = await api.unpublishPage(page.ID);
      setPage(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unpublish failed");
    } finally {
      setUnpublishing(false);
    }
  }

  async function handleDelete() {
    if (!page || !window.confirm(`Delete "${page.Title}"? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await api.deletePage(page.ID);
      window.location.href = "/app/pages";
    } catch (err) {
      setError(err instanceof Error ? err.message : "Delete failed");
      setDeleting(false);
    }
  }

  async function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id || !page) return;

    const activeParent = (active.data.current as { parentId?: string } | undefined)?.parentId;
    const overParent = (over.data.current as { parentId?: string } | undefined)?.parentId;

    const allBlocks = page.Blocks ?? [];

    if (activeParent === overParent) {
      // Same parent: reorder within the sibling list
      const siblings = activeParent
        ? allBlocks.filter(b => b.ParentBlockID === activeParent).sort((a, b) => (a.SortOrder ?? 0) - (b.SortOrder ?? 0))
        : allBlocks.filter(b => !b.ParentBlockID);

      const oldIdx = siblings.findIndex(b => b.ID === active.id);
      const newIdx = siblings.findIndex(b => b.ID === over.id);
      if (oldIdx === -1 || newIdx === -1) return;

      const reordered = [...siblings];
      const [moved] = reordered.splice(oldIdx, 1);
      reordered.splice(newIdx, 0, moved);

      const updatedBlocks = allBlocks.map(b => {
        const idx = reordered.findIndex(r => r.ID === b.ID);
        return idx !== -1 ? { ...b, SortOrder: idx } : b;
      });
      setPage({ ...page, Blocks: updatedBlocks, HasDraft: true });
      try {
        await api.reorderBlocks(page.ID, reordered.map(b => b.ID));
      } catch (err) {
        setError(err instanceof Error ? err.message : "Reorder failed");
        void load();
      }
    } else {
      // Cross-section: move block to a different parent
      const blockId = String(active.id);
      const newParent = overParent ?? null; // null = top-level

      // Optimistically update ParentBlockID so the tree re-renders immediately
      const updatedBlocks = allBlocks.map(b =>
        b.ID === blockId ? { ...b, ParentBlockID: newParent === null ? undefined : newParent } : b
      );
      setPage({ ...page, Blocks: updatedBlocks, HasDraft: true });
      try {
        await api.moveBlock(page.ID, blockId, newParent);
        void load();
      } catch (err) {
        setError(err instanceof Error ? err.message : "Move failed");
        void load();
      }
    }
  }

  async function handleSaveBlock(blockId: string, content: BlockContent) {
    if (!page) return;
    setSavingBlock(blockId);
    try {
      const updated = await api.updateBlock(page.ID, blockId, content);
      setPage({
        ...page,
        HasDraft: true,
        Blocks: page.Blocks.map((b) => b.ID === blockId ? { ...b, Content: updated.Content } : b),
      });
      setExpandedBlocks((prev) => { const next = new Set(prev); next.delete(blockId); return next; });
    } finally {
      setSavingBlock(null);
    }
  }

  async function handleDeleteBlock(blockId: string) {
    if (!page || !window.confirm("Delete this block?")) return;
    setDeletingBlock(blockId);
    try {
      await api.deleteBlock(page.ID, blockId);
      setPage({ ...page, HasDraft: true, Blocks: page.Blocks.filter((b) => b.ID !== blockId) });
      setExpandedBlocks((prev) => { const next = new Set(prev); next.delete(blockId); return next; });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Delete failed");
    } finally {
      setDeletingBlock(null);
    }
  }

  async function handleAddBlock(blockType: BlockType, content: BlockContent = {} as BlockContent, parentBlockId?: string) {
    if (!page) return;
    setAddingBlock(blockType);
    setError(null);
    try {
      const newBlock = await api.addBlock(page.ID, { block_type: blockType, content, parent_block_id: parentBlockId });
      setPage({ ...page, HasDraft: true, Blocks: [...(page.Blocks ?? []), newBlock] });
      setExpandedBlocks((prev) => new Set([...prev, newBlock.ID]));
      setShowAddBlock(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add block");
    } finally {
      setAddingBlock(null);
    }
  }

  function toggleBlock(id: string) {
    setExpandedBlocks((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  if (loading) return <p style={{ color: "var(--muted)" }}>Loading…</p>;
  if (!page) return <div className="alert alert-error">{error ?? "Page not found"}</div>;

  const allBlocks = page.Blocks ?? [];
  const blocks = allBlocks.filter(b => !b.ParentBlockID);
  const publicURL = portfolioSlugRef.current ? `/${portfolioSlugRef.current}/${page.Slug}` : undefined;
  const isLive = page.IsPublished;
  const hasDraft = page.HasDraft;
  // Publish button is primary when there are unpublished changes or the page has never been published.
  const publishIsPrimary = !isLive || hasDraft;

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1rem", flexWrap: "wrap" }}>
        <a href="/app/pages" style={{ color: "var(--muted)", fontSize: "0.9rem", textDecoration: "none" }}>← Pages</a>
        <div className="form-group" style={{ flex: 1, minWidth: 180, marginBottom: 0 }}>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Page title"
            style={{ fontSize: "1.1rem", fontWeight: 600 }}
          />
        </div>
        <div className="form-group" style={{ width: 180, marginBottom: 0 }}>
          <input
            value={page.Slug === "home" ? "/" : slug}
            onChange={page.Slug === "home" ? undefined : (e) => setSlug(e.target.value)}
            readOnly={page.Slug === "home"}
            placeholder="url-slug"
            style={{ fontFamily: "monospace", opacity: page.Slug === "home" ? 0.6 : 1, cursor: page.Slug === "home" ? "default" : undefined }}
            title={page.Slug === "home" ? "The home page is always served at /" : undefined}
          />
        </div>
        {/* Status badges */}
        {isLive && !hasDraft && (
          <span style={{ background: "#22c55e", color: "#fff", fontSize: "0.75rem", fontWeight: 600, padding: "0.2rem 0.55rem", borderRadius: "0.35rem", letterSpacing: "0.03em" }}>
            Live
          </span>
        )}
        {hasDraft && (
          <span
            onClick={() => publishBtnRef.current?.focus()}
            style={{ background: "#f59e0b", color: "#fff", fontSize: "0.75rem", fontWeight: 600, padding: "0.2rem 0.55rem", borderRadius: "0.35rem", letterSpacing: "0.03em", cursor: "pointer" }}
            title="Click to focus the Publish button"
          >
            {isLive ? "Live · changes pending" : "Draft"}
          </span>
        )}
        {!isLive && !hasDraft && (
          <span style={{ background: "var(--muted)", color: "#fff", fontSize: "0.75rem", fontWeight: 600, padding: "0.2rem 0.55rem", borderRadius: "0.35rem", letterSpacing: "0.03em" }}>
            Draft
          </span>
        )}
        {publicURL && (
          <a
            href={isLive ? publicURL : `${publicURL}?preview=true`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: "0.9rem" }}
          >
            View ↗
          </a>
        )}
        {/* Flash / auto-save indicator */}
        {saving && (
          <span style={{ color: "var(--muted)", fontSize: "0.85rem" }}>Saving…</span>
        )}
        {flash === "saved" && !saving && (
          <span style={{ color: "#22c55e", fontSize: "0.85rem", fontWeight: 500 }}>✓ Saved</span>
        )}
        {flash === "published" && (
          <span style={{ color: "#22c55e", fontSize: "0.85rem", fontWeight: 500 }}>✓ Published</span>
        )}
        {/* Action buttons — only shown when there's something to act on */}
        {(!isLive || hasDraft) && (
          <button
            className="btn-ghost"
            onClick={handleSaveDraft}
            disabled={saving}
            title="Save draft (⌘S)"
            style={{ fontSize: "0.9rem" }}
          >
            {saving ? "Saving…" : "Save draft"}
          </button>
        )}
        {(!isLive || hasDraft) && (
          <button
            ref={publishBtnRef}
            className={publishIsPrimary ? "btn-primary" : "btn-ghost"}
            onClick={handlePublish}
            disabled={publishing}
            style={{ fontSize: "0.9rem" }}
          >
            {publishing ? "Publishing…" : "Publish ↑"}
          </button>
        )}
        {isLive && (
          <button
            className="btn-ghost"
            onClick={handleUnpublish}
            disabled={unpublishing}
            style={{ fontSize: "0.85rem", color: "#ef4444" }}
          >
            {unpublishing ? "…" : "Unpublish"}
          </button>
        )}
        {page.Slug !== "home" && (
          <button
            className="btn-ghost"
            onClick={() => void handleDelete()}
            disabled={deleting}
            style={{ fontSize: "0.85rem", color: "#ef4444" }}
          >
            {deleting ? "…" : "Delete page"}
          </button>
        )}
      </div>

      {error && <div className="alert alert-error" style={{ marginBottom: "1rem" }}>{error}</div>}

      {/* SEO / Metadata */}
      <details style={{ marginBottom: "1.25rem" }}>
        <summary style={{
          cursor: "pointer",
          fontSize: "0.8rem",
          fontWeight: 600,
          color: "var(--muted)",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          userSelect: "none",
          padding: "0.4rem 0",
          listStyle: "none",
          display: "flex",
          alignItems: "center",
          gap: "0.4rem",
        }}>
          <span style={{ fontSize: "0.7rem" }}>▶</span> SEO &amp; metadata
          {(metaTitle || metaDesc) && (
            <span style={{ fontSize: "0.7rem", color: "var(--accent)", fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>
              · {metaTitle ? "title set" : ""}{metaTitle && metaDesc ? ", " : ""}{metaDesc ? "description set" : ""}
            </span>
          )}
        </summary>
        <div style={{ paddingTop: "0.75rem", display: "flex", flexDirection: "column", gap: "1rem" }}>
          <div className="form-group" style={{ margin: 0 }}>
            <label style={{ fontSize: "0.8rem" }}>
              Meta title
              <span style={{ color: metaTitle.length > 60 ? "#ef4444" : "var(--muted)", fontWeight: 400, marginLeft: "0.4rem", fontVariantNumeric: "tabular-nums" }}>
                ({metaTitle.length}/60)
              </span>
            </label>
            <input
              value={metaTitle}
              onChange={(e) => setMetaTitle(e.target.value)}
              onBlur={() => { if (page) void api.updatePage(page.ID, { meta_title: metaTitle, meta_description: metaDesc }).then(setPage).catch(() => {}); }}
              placeholder={`${title} — default if empty`}
              maxLength={80}
              style={{ fontSize: "0.875rem" }}
            />
            <small style={{ color: "var(--muted)", marginTop: "0.25rem", display: "block" }}>
              Shown as the browser tab title and in search results. Aim for 50–60 characters.
            </small>
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label style={{ fontSize: "0.8rem" }}>
              Meta description
              <span style={{ color: metaDesc.length > 155 ? "#ef4444" : "var(--muted)", fontWeight: 400, marginLeft: "0.4rem", fontVariantNumeric: "tabular-nums" }}>
                ({metaDesc.length}/155)
              </span>
            </label>
            <textarea
              value={metaDesc}
              onChange={(e) => setMetaDesc(e.target.value)}
              onBlur={() => { if (page) void api.updatePage(page.ID, { meta_title: metaTitle, meta_description: metaDesc }).then(setPage).catch(() => {}); }}
              placeholder="Short summary for search results"
              maxLength={200}
              rows={2}
              style={{ fontSize: "0.875rem", resize: "vertical" }}
            />
            <small style={{ color: "var(--muted)", marginTop: "0.25rem", display: "block" }}>
              Shown below the title in search results. Aim for up to 155 characters.
            </small>
          </div>
        </div>
      </details>

      {/* Block list */}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={blocks.map((b) => b.ID)} strategy={verticalListSortingStrategy}>
          {blocks.map((b) => (
            <div key={b.ID}>
              <SortableBlock
                block={b}
                expanded={expandedBlocks.has(b.ID)}
                onToggle={() => toggleBlock(b.ID)}
                onDelete={() => void handleDeleteBlock(b.ID)}
                onSave={(content) => handleSaveBlock(b.ID, content)}
                savingBlock={savingBlock === b.ID || deletingBlock === b.ID}
                packages={packages}
              />
              {b.BlockType === "section" && (
                <ChildTree
                  parentId={b.ID}
                  allBlocks={allBlocks}
                  depth={0}
                  expandedBlocks={expandedBlocks}
                  onToggle={toggleBlock}
                  onDelete={(id) => void handleDeleteBlock(id)}
                  onSave={handleSaveBlock}
                  onAddChild={(parentId, bt) => handleAddBlock(bt, {} as BlockContent, parentId)}
                  savingBlock={savingBlock}
                  addingBlock={addingBlock}
                  packages={packages}
                />
              )}
            </div>
          ))}
        </SortableContext>
      </DndContext>

      {blocks.length === 0 && !showAddBlock && (
        <div className="card" style={{ textAlign: "center", color: "var(--muted)", padding: "2rem" }}>
          No blocks yet. Add your first block below.
        </div>
      )}

      {/* Add block */}
      {showAddBlock ? (
        <div className="card" style={{ marginTop: "1rem" }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.75rem" }}>
            <strong>Choose block type</strong>
            <button className="btn-ghost" onClick={() => setShowAddBlock(false)}>✕ Close</button>
          </div>
          <p style={{ fontSize: "0.8rem", color: "var(--muted)", margin: "0 0 0.5rem" }}>
            Click an example to start with sample copy, or "Empty" for a blank block.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: "0.75rem" }}>
            {BLOCK_TYPES.map((bt) => {
              const examples = BLOCK_EXAMPLES[bt.type] ?? [];
              return (
                <div
                  key={bt.type}
                  style={{
                    border: "1px solid var(--border)",
                    borderRadius: "0.5rem",
                    padding: "0.75rem",
                    background: "var(--card)",
                    display: "flex", flexDirection: "column", gap: "0.4rem",
                    opacity: addingBlock === bt.type ? 0.6 : 1,
                  }}
                >
                  <div style={{ fontSize: "1.25rem" }}>{bt.icon}</div>
                  <div style={{ fontWeight: 600 }}>{bt.label}</div>
                  <div style={{ fontSize: "0.8rem", color: "var(--muted)" }}>{bt.description}</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "0.3rem", marginTop: "0.25rem" }}>
                    {examples.map((ex, idx) => (
                      <button
                        key={idx}
                        type="button"
                        className="btn-ghost"
                        disabled={addingBlock !== null}
                        onClick={() => void handleAddBlock(bt.type, ex.content)}
                        style={{ fontSize: "0.75rem", padding: "0.25rem 0.5rem" }}
                        title={ex.description}
                      >
                        ✨ {ex.label}
                      </button>
                    ))}
                    <button
                      type="button"
                      className="btn-ghost"
                      disabled={addingBlock !== null}
                      onClick={() => void handleAddBlock(bt.type)}
                      style={{ fontSize: "0.75rem", padding: "0.25rem 0.5rem" }}
                    >
                      Empty
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <button className="btn-ghost" onClick={() => setShowAddBlock(true)} style={{ marginTop: "1rem", width: "100%", padding: "0.75rem", border: "2px dashed var(--border)", borderRadius: "0.5rem" }}>
          + Add block
        </button>
      )}
    </div>
  );
}
